import { getPageLanguage } from '../utils/get-page-language.mjs';

let mapTemplate = null;
let mapTemplateOriginalViewBox = null;
let officeMarkerTemplate = null;
let offices = [];
let translations = {};

/**
 * @param {Element} rootElement
 */
export default async (rootElement = document.body) => {
  const blockElements = rootElement.querySelectorAll('.international-map-block');

  if(blockElements.length === 0) {
    return;
  }

  const promises = [
    loadSVG(import('../../assets/maps/spain-portugal.svg?raw')),
    loadSVG(import('../../assets/maps/office-marker.svg?raw')),
    loadOffices(),
    loadTranslations()
  ];

  [mapTemplate, officeMarkerTemplate, offices, translations] = await Promise.all(promises);

  if(!mapTemplate || !officeMarkerTemplate) {
    console.error('Failed to load map template or office marker template.');
    return;
  }

  mapTemplateOriginalViewBox = mapTemplate.querySelector('svg').getAttribute('viewBox');

  for(const blockElement of blockElements) {
    setupBlock(blockElement);
  }
};

const loadOffices = async () => {
  const culture = getPageLanguage();
  const url = `/api/${culture}/offices/international`;

  let response;
  try {
    response = await fetch(url);
  } catch(error) {
    if(error.name === 'AbortError') {
      return;
    }

    throw error;
  }

  const offices = await response.json();

  // Sort by x coordinate in descending order
  // to text going under icons (in most cases)
  offices.sort((a, b) => {
    return b.x - a.x;
  });

  return offices;
};

const loadTranslations = async () => {
  const culture = getPageLanguage();
  const url = `/api/${culture}/translations/international-map`;

  let response;
  try {
    response = await fetch(url);
  } catch(error) {
    if(error.name === 'AbortError') {
      return;
    }

    throw error;
  }

  return await response.json();
};

/**
 * Load SVG file using asset import
 * @param {Promise} promise An import promise with the SVG asset
 * @returns {Promise<SVGElement>} The SVG document as XMLDocument
 */
const loadSVG = async promise => {
  const text = (await promise).default;
  const fragment = document.createRange().createContextualFragment(text);
  return fragment;
};

const setupBlock = async blockElement => {
  const mapContainer = blockElement.querySelector('.map-container');
  mapContainer.appendChild(mapTemplate);

  setupOfficeMarkers(mapContainer);
  setupCountryTitles(mapContainer);
  setupRegionTitles(mapContainer);

  const officeMap = mapContainer.querySelector('svg');

  officeMap.addEventListener('click', e => {
    const currentMode = getMapMode(officeMap);
    const targetElement = e.target;

    if(currentMode === 'country-select') {
      const countryElement = targetElement.closest('.country');
      if(!countryElement) {
        return;
      }

      const countries = getAllCountries(officeMap);
      for(let country of countries) {
        if(country !== countryElement) {
          country.classList.add('disabled');
        }
      }

      setMapMode(officeMap, 'region-select');
    }

    if(currentMode === 'region-select') {
      const countryElement = targetElement.closest('.country');
      const regionElement = targetElement;
      const regionId = regionElement.id;

      if(countryElement.classList.contains('disabled')) {
        return;
      }

      if(!regionElement.classList.contains('has-office')) {
        return;
      }

      const allRegions = [...countryElement.querySelectorAll('path')];

      for(let region of allRegions) {
        if(region !== regionElement) {
          region.classList.add('disabled');
        }
      }

      const offices = [...officeMap.querySelectorAll('.office-marker')];
      for(let office of offices) {
        const showOffice = office.getAttribute('data-region-id') === regionId;
        office.querySelector('g').setAttribute('display', showOffice ? 'block' : 'none');
      }
      const boundingPadding = 4;
      const regionBoundingBox = regionElement.getBBox();
      officeMap.setAttribute('viewBox', `${regionBoundingBox.x - boundingPadding / 2} ${regionBoundingBox.y - boundingPadding / 2} ${regionBoundingBox.width + boundingPadding} ${regionBoundingBox.height + boundingPadding}`);

      setMapMode(officeMap, 'office-select');
    }
  });

  const resetButton = blockElement.querySelector('.reset-button');

  resetButton.addEventListener('click', () => {
    resetMap(officeMap);
  });
};

const setMapMode = (officeMap, mode) => {
  officeMap.classList.remove('country-select', 'region-select', 'office-select');
  officeMap.classList.add(mode);
};

const resetMap = (officeMap) => {
  const countries = getAllCountries(officeMap);
  for(let country of countries) {
    country.classList.remove('disabled');
  }

  const offices = [...officeMap.querySelectorAll('.office-marker')];
  for(let office of offices) {
    office.querySelector('g').setAttribute('display', 'none');
  }

  const regions = getAllRegions(officeMap);
  for(let region of regions) {
    region.classList.remove('disabled');
  }

  officeMap.setAttribute('viewBox', mapTemplateOriginalViewBox);

  setMapMode(officeMap, 'country-select');
};

const getMapMode = (officeMap) => officeMap.classList.contains('country-select') ? 'country-select' : officeMap.classList.contains('region-select') ? 'region-select' : 'office-select';
const getAllCountries = (officeMap) => [...officeMap.querySelectorAll('.country')];
const getAllRegions = (officeMap) => [...officeMap.querySelectorAll('.country > path')];

const setupOfficeMarkers = mapContainer => {
  const templateOfficeList = mapContainer.querySelector('.offices');

  for(let office of offices) {
    const officeMarker = officeMarkerTemplate.cloneNode(true);
    officeMarker.querySelector('g').setAttribute('display', 'none');
    officeMarker.querySelector('svg').setAttribute('x', office.x);
    officeMarker.querySelector('svg').setAttribute('y', office.y);
    officeMarker.querySelector('svg').setAttribute('data-region-id', office.regionId);

    officeMarker.querySelector('text').textContent = office.name;

    officeMarker.querySelector('a').setAttribute('href', office.url);

    templateOfficeList.appendChild(officeMarker);
  }

  const regionsWithOffices = new Set();
  for(let office of offices) {
    regionsWithOffices.add(office.regionId);
  }

  const officeMap = mapContainer.querySelector('svg');
  const regions = [...officeMap.querySelectorAll('.country > path')];
  for(let region of regions) {
    if(regionsWithOffices.has(region.id)) {
      region.classList.add('has-office');
    }
  }
};

const setupRegionTitles = (mapContainer) => {
  const countryRegions = getAllRegions(mapContainer);

  for(let region of countryRegions) {
    if(region.classList.contains('has-office')) {
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      const regionTitleContainer = region.closest('.country').querySelector('.region-titles');
      const regionTitle = translateRegionTitle(region.getAttribute('name'));
      const regionTitleParts = regionTitle.split(' ');

      const regionBBox = region.getBBox();
      const x = regionBBox.x + regionBBox.width / 2;
      const y = regionBBox.y + regionBBox.height / 2 - 5;

      const tspans = regionTitleParts.map(part => {
        const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        tspan.textContent = part;
        tspan.setAttribute('x', x);
        tspan.setAttribute('dy', '1em');
        return tspan;
      });
      text.append(...tspans);

      text.setAttribute('x', x);
      text.setAttribute('y', y);

      regionTitleContainer.appendChild(text);
    }
  }
};

const translateRegionTitle = (regionName) => {
  const translation = translations[`InternationalRegions.${regionName}`];
  if(!!translation) {
    return translation;
  }

  return regionName;
};

const setupCountryTitles = (mapContainer) => {
  const spainTitle = mapContainer.querySelector('.country-title.spain');
  spainTitle.textContent = translations['Countries.Spain'] || 'Spain';

  const portugalTitle = mapContainer.querySelector('.country-title.portugal');
  portugalTitle.textContent = translations['Countries.Portugal'] || 'Portugal';
};

